import Seo from "../components/Seo";
import { MarketingHeader, CTASection, FAQBlock } from "../components/MarketingShell";
import LegalFooter from "../components/LegalFooter";

const STEPS = [
  { n: "01", title: "Upload or paste", body: "Drag a video/audio file (up to 5 GB) or paste text directly. LinguaSub streams the upload to disk so browser tabs stay responsive even for hour-long files." },
  { n: "02", title: "Pick source + target language", body: "Any of the 21 supported languages either side. A one-click swap button flips them — 'Hindi → English' becomes 'English → Hindi' instantly. A guard prevents same-source-same-target." },
  { n: "03", title: "Whisper transcribes with timestamps", body: "For audio/video, ffmpeg extracts a compressed 16 kHz mono track, splits it into ≤ 24 MB chunks, and OpenAI Whisper transcribes each chunk with segment timestamps offset back to the original timeline." },
  { n: "04", title: "GPT-5.4 translates with personality", body: "The transcript is sent to GPT-5.4 with a subtitle-specific prompt: preserve tone, register, humour, sarcasm, honorifics, Bollywood expressions. Long transcripts are batched (60 segments per call) so an hour-long clip still finishes in a couple of minutes." },
  { n: "05", title: "Preview + edit against the video", body: "The Edit & Render panel shows the video with each segment as a clickable card. Click a line → the video pauses and jumps to that segment. Edit the translated text, tweak start/end. Save whenever you want." },
  { n: "06", title: "Render subtitled MP4", body: "One click triggers a background ffmpeg job that burns subtitles into the frames (libx264, white text with black outline, 40 px margin). The frontend polls a status endpoint so you know when it's ready." },
  { n: "07", title: "Download SRT, VTT, or subtitled MP4", body: "Three files ready: the original SRT for YouTube Studio, the VTT for web players like Vimeo, and the burned-in MP4 for Reels, TikTok, or WhatsApp forwards." },
];

const FAQ = [
  { q: "How long does a typical video take?", a: "A 5-minute video: ~30 seconds transcription + ~15 seconds translation + ~20 seconds burn-in ≈ 1 minute total. A 60-minute podcast: ~5 minutes transcription + ~1 minute translation + ~4 minutes burn-in ≈ 10 minutes." },
  { q: "Do I have to be online for the whole time?", a: "The upload and processing happen server-side, so once the upload completes you can close the tab. Come back later — your job is in the History panel with the SRT/VTT ready." },
  { q: "Can I edit subtitles without re-rendering the video?", a: "Yes. You can save edits and download the updated SRT/VTT without triggering a burn-in. Render is only needed if you want the finished MP4." },
  { q: "What if the transcription misses a word?", a: "That's what the editor is for. Every segment is editable in place — the translated text, the start time, and the end time. Save your fix, and the SRT/VTT regenerate instantly." },
  { q: "Can I do batches or automate it?", a: "Web UI is one file at a time right now. A public API is on the roadmap for Studio and Agency plans." },
];

const ENGINE_LAYERS = [
  { n: "01", title: "Understand", body: "Detect speech and speakers — figure out who is talking so translations can hold voice across turns." },
  { n: "02", title: "Transcribe", body: "Generate an accurate transcript in the source language with word-level timestamps." },
  { n: "03", title: "Understand context", body: "Extract characters, relationships, scene and emotion so the translation reads in-character." },
  { n: "04", title: "Translate", body: "Turn source lines into natural, native-sounding target lines using the story brief and speaker tags." },
  { n: "05", title: "Localize", body: "Adapt slang, humour, idioms and cultural references so jokes actually land." },
  { n: "06", title: "Subtitle", body: "Optimise line breaks and reading speed — cap at 42 chars per line, 17 chars per second." },
  { n: "07", title: "Quality check", body: "Verify timing, grammar, terminology consistency and enforce the caption spec end-to-end." },
];

export default function HowItWorksPage() {
  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white">
      <Seo path="/how-it-works" title="How it works" description="How LinguaSub turns a Hindi, Tamil, Bengali or English video into subtitled output in 7 steps — upload, transcribe with Whisper, translate with GPT-5.4, edit, render, download." />
      <MarketingHeader />
      <section className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">The workflow</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter leading-tight mb-6 max-w-4xl">
            From raw upload to shipped subtitles in seven steps.
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed">
            Under the hood: ffmpeg for media plumbing, OpenAI Whisper for transcription, GPT-5.4 for personality-preserving translation, and libx264 for the final burn-in.
          </p>
        </div>
      </section>
      <section className="border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="space-y-8">
            {STEPS.map((s) => (
              <div key={s.n} className="grid grid-cols-[auto_1fr] gap-6 md:gap-8 items-start">
                <div className="font-heading text-5xl md:text-6xl font-black tracking-tighter text-blue-500/80">{s.n}</div>
                <div>
                  <h3 className="font-heading text-2xl font-bold text-white mb-2">{s.title}</h3>
                  <p className="text-sm md:text-base text-zinc-400 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech stack callout */}
      <section className="border-b border-zinc-800 bg-zinc-900/40">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mb-8 text-white">
            What's actually running.
          </h2>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { k: "Transcription", v: "OpenAI Whisper (whisper-1), verbose_json with segment timestamps, chunked for long audio." },
              { k: "Translation", v: "OpenAI GPT-5.4 via Emergent Universal Key with a subtitle-tuned system prompt." },
              { k: "Video processing", v: "ffmpeg 5.1 for audio extraction and burn-in. libx264 with ultrafast preset, CRF 26." },
              { k: "Storage", v: "MongoDB for jobs, transient disk for uploads (48h TTL), Emergent object storage optional." },
            ].map((r) => (
              <div key={r.k}>
                <dt className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-1">{r.k}</dt>
                <dd className="text-sm text-zinc-300 leading-relaxed">{r.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* LinguaSub Engine — 7 layers */}
      <section className="border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-3">
            The LinguaSub Engine
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-4 text-white max-w-3xl">
            Seven layers between raw speech and shippable subtitles.
          </h2>
          <p className="text-zinc-400 text-base md:text-lg max-w-2xl leading-relaxed mb-10">
            Every job runs through a purpose-built pipeline. Each layer is
            visible in the app so you can see exactly what changed.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ENGINE_LAYERS.map((s) => (
              <div key={s.n} className="border border-zinc-800 bg-zinc-900/40 p-6">
                <div className="flex items-baseline gap-3 mb-2">
                  <div className="font-heading text-3xl font-black tracking-tighter text-emerald-400">
                    {s.n}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white">{s.title}</h3>
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQBlock items={FAQ} />
      <CTASection title="Ready to see it run?" subtitle="One-time free trial for new users. Upload a clip and watch each step happen live." />
      <div className="max-w-6xl mx-auto px-4 md:px-8"><LegalFooter /></div>
    </div>
  );
}
